/**
 * Utility functions for handling SQL Null types from Go backend
 */

import type { SqlNullString, SqlNullInt32, SqlNullFloat64, SqlNullTime, SqlNullBool } from '../types';

// Re-export types for convenience
export type { SqlNullString, SqlNullInt32, SqlNullFloat64, SqlNullTime, SqlNullBool };

// Legacy alias for backwards compatibility
export type SqlNullInt64 = SqlNullInt32;

/**
 * Extract string value from SQL NullString or plain string
 * @param value - Can be SqlNullString, string, or null/undefined
 * @param fallback - Default value if null or invalid
 * @returns Extracted string or fallback
 */
export const extractString = (value: SqlNullString | string | null | undefined, fallback: string = ''): string => {
  if (!value) return fallback;
  
  // If it's a SQL NullString object with {String: "...", Valid: true/false}
  if (typeof value === 'object' && 'String' in value && 'Valid' in value) {
    return value.Valid ? (value.String || fallback) : fallback;
  }
  
  // If it's already a plain string
  if (typeof value === 'string') {
    return value;
  }
  
  return fallback;
};

/**
 * Extract number value from SQL NullInt32/64 or plain number
 * @param value - Can be SqlNullInt32/64, number, or null/undefined
 * @param fallback - Default value if null or invalid
 * @returns Extracted number or fallback
 */
export const extractNumber = (value: SqlNullInt32 | SqlNullInt64 | number | null | undefined, fallback: number = 0): number => {
  if (value === null || value === undefined) return fallback;

  // If it's a SQL NullInt32 object
  if (typeof value === 'object' && 'Int32' in value && 'Valid' in value) {
    return value.Valid ? (value.Int32 ?? fallback) : fallback;
  }

  // If it's already a plain number
  if (typeof value === 'number') {
    return value;
  }

  return fallback;
};

/**
 * Extract integer value from SQL NullInt32/64 or plain number
 * Alias for extractNumber for clarity
 */
export const extractInt = extractNumber;

/**
 * Extract float value from SQL NullFloat64 or plain number
 * @param value - Can be SqlNullFloat64, number, or null/undefined
 * @param fallback - Default value if null or invalid
 * @returns Extracted number or null if not valid and no fallback
 */
export const extractFloat = (value: SqlNullFloat64 | number | null | undefined, fallback?: number): number | null => {
  if (value === null || value === undefined) return fallback ?? null;

  // If it's a SQL NullFloat64 object
  if (typeof value === 'object' && 'Float64' in value && 'Valid' in value) {
    return value.Valid ? (value.Float64 ?? fallback ?? null) : (fallback ?? null);
  }

  // If it's already a plain number
  if (typeof value === 'number') {
    return value;
  }

  return fallback ?? null;
};

/**
 * Extract Date from SQL NullTime or plain string/Date
 * @param value - Can be SqlNullTime, string, Date, or null/undefined
 * @param fallback - Default value if null or invalid
 * @returns Extracted Date or fallback
 */
export const extractDate = (value: SqlNullTime | string | Date | null | undefined, fallback?: Date): Date | null => {
  if (!value) return fallback || null;
  
  // If it's a SQL NullTime object
  if (typeof value === 'object' && 'Time' in value && 'Valid' in value) {
    return value.Valid ? new Date(value.Time) : (fallback || null);
  }
  
  // If it's a Date object
  if (value instanceof Date) {
    return value;
  }
  
  // If it's a string
  if (typeof value === 'string') {
    return new Date(value);
  }
  
  return fallback || null;
};

/**
 * Type guard to check if value is a SQL Null wrapper object
 */
interface SqlNullWrapper {
  Valid: boolean;
  [key: string]: unknown;
}

const isSqlNullWrapper = (value: unknown): value is SqlNullWrapper => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'Valid' in value &&
    typeof (value as SqlNullWrapper).Valid === 'boolean'
  );
};

/**
 * Check if a SQL Null value is valid (not null)
 * @param value - Any SQL Null type
 * @returns True if value is valid (not null)
 */
export const isValidSqlNull = (value: unknown): boolean => {
  if (!value) return false;
  if (isSqlNullWrapper(value)) {
    return value.Valid === true;
  }
  return true; // If it's not a SQL Null type, consider it valid
};
